import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount } from "wagmi";
import {
  deleteAsset as apiDelete,
  fetchUsage,
  listAssets,
  MediaApiError,
  type MediaAsset,
  type MediaUsage,
  uploadAsset as apiUpload,
} from "../../lib/media-api";

const PAGE_SIZE = 12;

export interface MediaLibraryState {
  status: "loading" | "ready" | "error";
  usage: MediaUsage | null;
  assets: MediaAsset[];
  nextCursor: string | null;
  error: string | null;
  busy: boolean;
}

/**
 * Media Library data hook with STRICT wallet-context isolation.
 *
 * Every async result is tagged with a monotonic epoch bound to the connected
 * wallet. A response that resolves after a wallet switch (or unmount) is
 * DROPPED — a late wallet-A list can never render over wallet-B, even
 * transiently. In-flight requests are aborted on context change.
 */
export function useMediaLibrary() {
  const { address } = useAccount();
  const [state, setState] = useState<MediaLibraryState>({
    status: "loading", usage: null, assets: [], nextCursor: null, error: null, busy: false,
  });

  const epochRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; abortRef.current?.abort(); };
  }, []);

  const fresh = useCallback((epoch: number) => mountedRef.current && epoch === epochRef.current, []);

  const reload = useCallback(async () => {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    const epoch = epochRef.current;
    setState((s) => ({ ...s, status: "loading", error: null }));
    try {
      const [usage, page] = await Promise.all([
        fetchUsage(ac.signal),
        listAssets(null, PAGE_SIZE, ac.signal),
      ]);
      if (!fresh(epoch)) return;
      setState({ status: "ready", usage, assets: page.assets ?? [], nextCursor: page.next_cursor ?? null, error: null, busy: false });
    } catch (e) {
      if (ac.signal.aborted || !fresh(epoch)) return;
      setState((s) => ({ ...s, status: "error", error: msg(e) }));
    }
  }, [fresh]);

  // Reset + reload whenever the connected wallet changes (context isolation).
  useEffect(() => {
    epochRef.current += 1;
    setState({ status: "loading", usage: null, assets: [], nextCursor: null, error: null, busy: false });
    if (address) void reload();
  }, [address, reload]);

  const loadMore = useCallback(async () => {
    const cursor = state.nextCursor;
    if (!cursor) return;
    const epoch = epochRef.current;
    const ac = new AbortController();
    try {
      const page = await listAssets(cursor, PAGE_SIZE, ac.signal);
      if (!fresh(epoch)) return;
      setState((s) => ({ ...s, assets: [...s.assets, ...(page.assets ?? [])], nextCursor: page.next_cursor ?? null }));
    } catch (e) {
      if (!fresh(epoch)) return;
      setState((s) => ({ ...s, error: msg(e) }));
    }
  }, [state.nextCursor, fresh]);

  const upload = useCallback(async (file: File): Promise<{ ok: boolean; error?: string }> => {
    const epoch = epochRef.current;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      await apiUpload(file);
      if (!fresh(epoch)) return { ok: true };
      await reload();
      return { ok: true };
    } catch (e) {
      if (fresh(epoch)) setState((s) => ({ ...s, busy: false, error: msg(e) }));
      return { ok: false, error: msg(e) };
    }
  }, [fresh, reload]);

  const remove = useCallback(async (assetId: string): Promise<{ ok: boolean; error?: string }> => {
    const epoch = epochRef.current;
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      await apiDelete(assetId);
      if (!fresh(epoch)) return { ok: true };
      await reload();
      return { ok: true };
    } catch (e) {
      const message = msg(e);
      if (fresh(epoch)) setState((s) => ({ ...s, busy: false, error: message }));
      return { ok: false, error: message };
    }
  }, [fresh, reload]);

  return { state, reload, loadMore, upload, remove };
}

function msg(e: unknown): string {
  if (e instanceof MediaApiError) {
    switch (e.code) {
      case "MEDIA_QUOTA_EXCEEDED": return "Not enough storage in your 2 GiB library.";
      case "MEDIA_TOO_LARGE": return "File exceeds the allowed size for its type.";
      case "MEDIA_UNSUPPORTED_TYPE": return "Unsupported media type.";
      case "MEDIA_INVALID": return "File could not be validated.";
      case "MEDIA_PROCESSING_FAILED": return "Media processing failed. Please try another file.";
      case "MEDIA_ASSET_IN_USE": return "This asset is in use and cannot be deleted.";
      default: return e.message || "Media request failed.";
    }
  }
  return "Media request failed.";
}
